// Design tokens taken verbatim from the client's prototype (My Dent.dc.html, `:root` and
// `[data-theme-mode="dark"]`). Keep these in sync with the prototype rather than tweaking ad hoc.

export interface DesignTokens {
  bg: string;
  panel: string;
  panel2: string;
  panel3: string;
  text: string;
  muted: string;
  faint: string;
  border: string;
  borderStrong: string;
  primary: string;
  primaryBright: string;
  primaryStrong: string;
  primary050: string;
  primary100: string;
  primaryInk: string;
  ok: string;
  ok050: string;
  bad: string;
  bad050: string;
  warn: string;
  okInk: string;
  badInk: string;
  warnInk: string;
  violetInk: string;
  blueInk: string;
  gradSidebar: string;
  gradPrimary: string;
  gradAvatar: string;
  /**
   * The three elevation slots. Kept very soft: the hairline borders still do most of the work,
   * the shadow only lifts surfaces slightly off the page.
   */
  shadowSm: string;
  shadow: string;
  /** Between `shadow` and `shadowLg`: dashboard cards that should sit a little higher. */
  shadowRaised: string;
  shadowLg: string;
}

const gradPrimary = 'linear-gradient(135deg,#6E8A00 0%,#557300 60%,#485F00 100%)';
const gradAvatar = 'linear-gradient(135deg,#B0C810 0%,#557300 100%)';

export const lightTokens: DesignTokens = {
  bg: '#F4F6FB',
  panel: '#FFFFFF',
  panel2: '#F6F8FC',
  panel3: '#EEF2F8',
  text: '#0F172A',
  muted: '#4A5A74',
  faint: '#64738A',
  border: '#E7ECF4',
  borderStrong: '#D6DEEA',
  primary: '#557300',
  primaryBright: '#B0C810',
  primaryStrong: '#445C00',
  primary050: '#F4F8E4',
  primary100: '#E7F0C6',
  primaryInk: '#3A4F00',
  ok: '#10B981',
  ok050: '#E9FBF3',
  bad: '#F43F5E',
  bad050: '#FEF0F2',
  warn: '#F59E0B',
  okInk: '#08694A',
  badInk: '#9C0C26',
  warnInk: '#734500',
  violetInk: '#5B2FBA',
  blueInk: '#1A4FB0',
  gradSidebar: 'linear-gradient(172deg,#1B2410 0%,#232E12 48%,#2A3714 100%)',
  gradPrimary,
  gradAvatar,
  shadowSm: '0 1px 2px rgba(15,23,42,.04)',
  shadow: '0 1px 2px rgba(15,23,42,.04), 0 1px 3px rgba(15,23,42,.05)',
  shadowRaised: '0 2px 6px rgba(15,23,42,.06), 0 1px 2px rgba(15,23,42,.05)',
  shadowLg: '0 4px 12px rgba(15,23,42,.07), 0 1px 3px rgba(15,23,42,.05)',
};

export const darkTokens: DesignTokens = {
  bg: '#080D18',
  panel: '#0F1626',
  panel2: '#131C2E',
  panel3: '#182236',
  text: '#E9EEF7',
  muted: '#A7B4CA',
  faint: '#8795AF',
  border: 'rgba(255,255,255,.08)',
  borderStrong: 'rgba(255,255,255,.15)',
  primary: '#C3DB3E',
  primaryBright: '#B0C810',
  primaryStrong: '#9BB000',
  primary050: 'rgba(195,219,62,.12)',
  primary100: 'rgba(195,219,62,.2)',
  primaryInk: '#DCEB8A',
  ok: '#34D399',
  ok050: 'rgba(52,211,153,.14)',
  bad: '#FB7185',
  bad050: 'rgba(251,113,133,.15)',
  warn: '#F59E0B',
  okInk: '#5FE0AE',
  badInk: '#FF9AAA',
  warnInk: '#F5C05C',
  violetInk: '#BCA4FF',
  blueInk: '#93BAFF',
  gradSidebar: 'linear-gradient(172deg,#141A0E 0%,#1A2211 60%,#202912 100%)',
  gradPrimary,
  gradAvatar,
  shadowSm: '0 1px 2px rgba(0,0,0,.25)',
  shadow: '0 1px 2px rgba(0,0,0,.25), 0 1px 4px rgba(0,0,0,.2)',
  shadowRaised: '0 3px 10px rgba(0,0,0,.3), 0 1px 3px rgba(0,0,0,.25)',
  shadowLg: '0 6px 16px rgba(0,0,0,.35), 0 1px 4px rgba(0,0,0,.25)',
};

/** Categorical hues the prototype uses for icon chips, chart series and location dots. */
export const accentHues = {
  green: '#7E9B00',
  blue: '#3B82F6',
  violet: '#8B5CF6',
  amber: '#F59E0B',
  rose: '#F43F5E',
  emerald: '#10B981',
  slate: '#94A3B8',
} as const;

/**
 * Corner radii. Deliberately tight — the UI uses barely-rounded corners, so every component reads
 * from this scale rather than hard-coding pixels; changing the feel is a one-line change here.
 * They are px strings, not numbers: inside `sx`, MUI multiplies a numeric `borderRadius` by
 * `theme.shape.borderRadius`, which silently turned `6` into 24px.
 * `pill` stays fully round and is only for genuinely circular things (avatars, progress bars, dots).
 */
export const radius = {
  card: '4px',
  control: '3px',
  input: '3px',
  popover: '4px',
  sm: '2px',
  xs: '2px',
  pill: 999,
} as const;

/**
 * Control heights. Every button, input, select, search field, chip and segmented control reads
 * from this, so controls placed side by side in a toolbar always line up. `md` is the default,
 * `sm` is for dense spots (table rows, sheets, compact filter rows).
 */
export const controlHeight = { md: 40, sm: 34 } as const;

export const fonts = {
  head: '"Plus Jakarta Sans Variable", system-ui, sans-serif',
  body: '"Manrope Variable", system-ui, sans-serif',
} as const;
